import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spinner, Badge } from 'react-bootstrap';
import Layout from '../components/layouts/Main';
import axios from 'axios';
import config from '../config';
import { toast } from 'react-toastify';
import { 
  FiRefreshCw, 
  FiPieChart, 
  FiBarChart2, 
  FiTrendingUp, 
  FiActivity, 
  FiPackage,
  FiShoppingBag,
  FiTruck
} from 'react-icons/fi';

const ReportingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    salesOrders: [],
    purchaseOrders: [],
    stock: [],
    buyers: [],
    vendors: [],
    brokers: []
  });

  useEffect(() => {
    if (!window.echarts) {
      const script = document.createElement('script');
      script.src = '/assets/vendors/echarts/echarts.min.js';
      script.async = true;
      script.onload = () => fetchDashboardData();
      document.body.appendChild(script);
    } else {
      fetchDashboardData();
    }

    const handleResize = () => {
      const charts = document.querySelectorAll('.echart-container');
      charts.forEach(chartDom => {
        const chart = window.echarts?.getInstanceByDom(chartDom);
        chart?.resize();
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      const containers = document.querySelectorAll('.echart-container');
      containers.forEach(dom => window.echarts?.getInstanceByDom(dom)?.dispose());
    };
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [soRes, poRes, stockRes, buyersRes, vendorsRes, brokersRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/sales-orders?populate=true&populateItems=true`),
        axios.get(`${config.API_BASE_URL}/purchase-orders?populate=true&populateItems=true`),
        axios.get(`${config.API_BASE_URL}/inventory/stock-overview`),
        axios.get(`${config.API_BASE_URL}/buyers`),
        axios.get(`${config.API_BASE_URL}/vendors`),
        axios.get(`${config.API_BASE_URL}/brokers`)
      ]);

      // Helper to calculate PO Grand Total consistently
      const calculatePOTotal = (po) => {
        if (po.finalPrice) return parseFloat(po.finalPrice);
        if (po.itemsTotalPrice) {
          const tax = parseFloat(po.tax) || 0;
          return po.itemsTotalPrice + (po.itemsTotalPrice * (tax / 100));
        }
        if (po.items && po.items.length > 0) {
          const subtotal = po.items.reduce((sum, it) => sum + ((it.orderedQuantity || 0) * (it.rate || 0)), 0);
          const tax = parseFloat(po.tax) || 0;
          return subtotal + (subtotal * (tax / 100));
        }
        return 0;
      };

      const newData = {
        salesOrders: (soRes.data || []).map(so => ({
          ...so,
          calculatedTotal: parseFloat(so.totalAmount) || 0
        })),
        purchaseOrders: (poRes.data || []).map(po => ({
          ...po,
          calculatedTotal: calculatePOTotal(po)
        })),
        stock: stockRes.data.products || [],
        reservedStock: stockRes.data.reservedStock || {},
        inProductionStock: stockRes.data.inProductionStock || {},
        buyers: buyersRes.data || [],
        vendors: vendorsRes.data || [],
        brokers: brokersRes.data || []
      };

      setData(newData);
      setTimeout(() => renderAllCharts(newData), 200);
    } catch (error) {
      console.error("Error fetching dashboard data", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const renderAllCharts = (d) => {
    if (!window.echarts) return;

    renderOrderValueTrends(d);
    renderSalesPipeline(d);
    renderTopBuyers(d);
    renderTopVendors(d);
    renderStockHealth(d);
    renderTopProducts(d);
    renderStockComposition(d);
    renderBrokerPerformance(d);
  };

  // 1. Line Chart: 6-Month Value Trends
  const renderOrderValueTrends = (d) => {
    const chart = window.echarts.init(document.getElementById('orderTrends'));
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const last6Months = [];
    const now = new Date();
    
    for(let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last6Months.push({
        label: `${months[date.getMonth()]} ${date.getFullYear()}`,
        month: date.getMonth(),
        year: date.getFullYear()
      });
    }

    const sales = last6Months.map(m => {
      return d.salesOrders
        .filter(so => {
          // Exclude cancelled or failed orders
          if (['Cancelled', 'QC Failed'].includes(so.status)) return false;
          
          const dso = new Date(so.orderDate || so.createdAt);
          return dso.getMonth() === m.month && dso.getFullYear() === m.year;
        })
        .reduce((sum, so) => sum + so.calculatedTotal, 0);
    });

    const purchases = last6Months.map(m => {
      return d.purchaseOrders
        .filter(po => {
          // Exclude cancelled POs
          if (po.status === 'Cancelled') return false;
          
          const dpo = new Date(po.orderDate || po.createdAt);
          return dpo.getMonth() === m.month && dpo.getFullYear() === m.year;
        })
        .reduce((sum, po) => sum + po.calculatedTotal, 0);
    });

    chart.setOption({
      title: { text: 'Financial Trends (Sales vs Purchases)', left: 'center', textStyle: { fontSize: 16 } },
      tooltip: { 
        trigger: 'axis', 
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        textStyle: { color: '#333' },
        formatter: (params) => {
          let res = `<strong>${params[0].name}</strong><br/>`;
          params.forEach(p => {
            res += `${p.marker} ${p.seriesName}: <strong>₹${p.value.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong><br/>`;
          });
          return res;
        }
      },
      legend: { bottom: 5, data: ['Sales', 'Purchases'] },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: last6Months.map(m => m.label), boundaryGap: false },
      yAxis: { 
        type: 'value', 
        axisLabel: { 
          formatter: (v) => '₹' + (v >= 100000 ? (v/100000).toFixed(1) + 'L' : v >= 1000 ? (v/1000).toFixed(0) + 'k' : v) 
        } 
      },
      series: [
        { 
          name: 'Sales', 
          type: 'line', 
          data: sales, 
          smooth: true, 
          symbolSize: 8,
          lineStyle: { width: 3, shadowBlur: 10, shadowColor: 'rgba(52, 152, 219, 0.3)' },
          itemStyle: { color: '#3498db' }, 
          areaStyle: { 
            color: new window.echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(52, 152, 219, 0.3)' },
              { offset: 1, color: 'rgba(52, 152, 219, 0)' }
            ])
          } 
        },
        { 
          name: 'Purchases', 
          type: 'line', 
          data: purchases, 
          smooth: true, 
          symbolSize: 8,
          lineStyle: { width: 3, shadowBlur: 10, shadowColor: 'rgba(46, 204, 113, 0.3)' },
          itemStyle: { color: '#2ecc71' }, 
          areaStyle: { 
            color: new window.echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(46, 204, 113, 0.3)' },
              { offset: 1, color: 'rgba(46, 204, 113, 0)' }
            ])
          } 
        }
      ]
    });
  };

  // 2. Funnel Chart: Sales Pipeline
  const renderSalesPipeline = (d) => {
    const chart = window.echarts.init(document.getElementById('salesPipeline'));
    const statusCounts = {};
    d.salesOrders.forEach(so => {
      const s = so.status || 'Other';
      statusCounts[s] = (statusCounts[s] || 0) + 1;
    });

    const funnelData = [
      { name: 'New/Created', value: (statusCounts['Created'] || 0) + (statusCounts['New'] || 0) },
      { name: 'Processing/Confirmed', value: (statusCounts['Confirmed'] || 0) + (statusCounts['Processing'] || 0) + (statusCounts['Pending'] || 0) },
      { name: 'Dispatched/Loaded', value: (statusCounts['Dispatched'] || 0) + (statusCounts['Loaded'] || 0) },
      { name: 'Completed/Delivered', value: (statusCounts['Completed'] || 0) + (statusCounts['Delivered'] || 0) }
    ].filter(i => i.value > 0);

    chart.setOption({
      title: { text: 'Sales Pipeline (Volume)', left: 'center' },
      tooltip: { trigger: 'item', formatter: '{b} : {c} Orders' },
      series: [{
        name: 'Pipeline',
        type: 'funnel',
        left: '10%', top: 60, bottom: 60, width: '80%',
        sort: 'descending',
        data: funnelData
      }]
    });
  };

  // 3. Pie Chart: Top Buyers
  const renderTopBuyers = (d) => {
    const chart = window.echarts.init(document.getElementById('salesByBuyer'));
    const aggregation = {};
    d.salesOrders.forEach(so => {
      const name = so.buyer?.business_name || 'Unknown';
      aggregation[name] = (aggregation[name] || 0) + so.calculatedTotal;
    });

    const sortedData = Object.entries(aggregation)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    chart.setOption({
      title: { text: 'Top 5 Buyers (Sales Value)', left: 'center' },
      tooltip: { trigger: 'item', formatter: (p) => `${p.name}: ₹${p.value.toLocaleString()} (${p.percent}%)` },
      legend: { orient: 'vertical', left: 'left', top: 'middle', itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 10 } },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['60%', '55%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        data: sortedData
      }]
    });
  };

  // 4. Bar Chart: Top Vendors
  const renderTopVendors = (d) => {
    const chart = window.echarts.init(document.getElementById('purchasesByVendor'));
    const aggregation = {};
    d.purchaseOrders.forEach(po => {
      const name = po.vendorId?.business_name || 'Unknown';
      aggregation[name] = (aggregation[name] || 0) + po.calculatedTotal;
    });

    const sortedData = Object.entries(aggregation)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    chart.setOption({
      title: { text: 'Top 5 Vendors (Purchase Value)', left: 'center' },
      tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ₹${p[0].value.toLocaleString()}` },
      xAxis: { type: 'value', axisLabel: { show: false } },
      yAxis: { type: 'category', data: sortedData.map(i => i.name).reverse() },
      series: [{
        name: 'Purchases',
        type: 'bar',
        data: sortedData.map(i => i.value).reverse(),
        itemStyle: { 
          color: new window.echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: '#2ecc71' },
            { offset: 1, color: '#27ae60' }
          ])
        },
        label: { show: true, position: 'right', formatter: (p) => '₹' + (p.value >= 1000 ? (p.value/1000).toFixed(0) + 'k' : p.value) }
      }]
    });
  };

  // 5. Donut Chart: Inventory Health
  const renderStockHealth = (d) => {
    const chart = window.echarts.init(document.getElementById('stockHealth'));
    let inStock = 0, lowStock = 0, outOfStock = 0;

    d.stock.forEach(item => {
      const total = (item.packedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0) +
                    (item.unpackedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0) +
                    (item.inPackingStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
      const buffer = item.product?.bufferStock || 0;

      if (total <= 0) outOfStock++;
      else if (total <= buffer) lowStock++;
      else inStock++;
    });

    chart.setOption({
      title: { text: 'Inventory Health', left: 'center' },
      tooltip: { trigger: 'item' },
      legend: { bottom: 0 },
      color: ['#2ecc71', '#f1c40f', '#e74c3c'],
      series: [{
        type: 'pie',
        radius: ['50%', '70%'],
        data: [
          { value: inStock, name: 'In Stock' },
          { value: lowStock, name: 'Low Stock' },
          { value: outOfStock, name: 'Out of Stock' }
        ]
      }]
    });
  };

  // 6. Horizontal Bar: Top Products
  const renderTopProducts = (d) => {
    const chart = window.echarts.init(document.getElementById('topProducts'));
    const aggregation = {};
    
    d.salesOrders.forEach(so => {
      (so.items || []).forEach(item => {
        const name = item.product?.productName || 'Unknown';
        const itemVal = item.totalPrice || (parseFloat(item.quantity) * parseFloat(item.unitPrice)) || 0;
        aggregation[name] = (aggregation[name] || 0) + itemVal;
      });
    });

    const sortedData = Object.entries(aggregation)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);

    chart.setOption({
      title: { text: 'Top Selling Products (by Value)', left: 'center' },
      tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: ₹${p[0].value.toLocaleString()}` },
      grid: { left: '3%', right: '10%', bottom: '3%', containLabel: true },
      xAxis: { type: 'value', axisLabel: { formatter: (v) => '₹' + (v >= 1000 ? (v/1000).toFixed(0) + 'k' : v) } },
      yAxis: { type: 'category', data: sortedData.map(i => i.name).reverse() },
      series: [{
        type: 'bar',
        data: sortedData.map(i => i.value).reverse(),
        itemStyle: { color: '#9b59b6' },
        label: { show: true, position: 'right', formatter: (p) => '₹' + (p.value >= 1000 ? (p.value/1000).toFixed(1) + 'k' : p.value) }
      }]
    });
  };

  // 7. Stacked Bar: Stock Composition
  const renderStockComposition = (d) => {
    const chart = window.echarts.init(document.getElementById('stockComp'));
    const top10Products = [...d.stock]
      .sort((a, b) => (b.totalAvailableStock || 0) - (a.totalAvailableStock || 0))
      .slice(0, 10);

    const names = top10Products.map(p => p.product?.productName || 'Unknown');
    const packed = top10Products.map(p => (p.packedStock || []).reduce((s, i) => s + (i.availableStock || 0), 0));
    const unpacked = top10Products.map(p => (p.unpackedStock || []).reduce((s, i) => s + (i.availableStock || 0), 0));
    const inPacking = top10Products.map(p => (p.inPackingStock || []).reduce((s, i) => s + (i.availableStock || 0), 0));

    chart.setOption({
      title: { text: 'Stock Composition (Top 10 Products)', left: 'center' },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { bottom: 0, data: ['Packed', 'Unpacked', 'In-Packing'] },
      grid: { bottom: 80, left: '3%', right: '4%', containLabel: true },
      xAxis: { type: 'category', data: names, axisLabel: { rotate: 30, interval: 0, fontSize: 10 } },
      yAxis: { type: 'value' },
      series: [
        { name: 'Packed', type: 'bar', stack: 'total', data: packed, itemStyle: { color: '#27ae60' } },
        { name: 'Unpacked', type: 'bar', stack: 'total', data: unpacked, itemStyle: { color: '#f39c12' } },
        { name: 'In-Packing', type: 'bar', stack: 'total', data: inPacking, itemStyle: { color: '#3498db' } }
      ]
    });
  };

  // 8. Pie Chart: Broker performance
  const renderBrokerPerformance = (d) => {
    const chart = window.echarts.init(document.getElementById('brokerPerformance'));
    const aggregation = {};
    d.salesOrders.forEach(so => {
      const name = so.broker?.business_name || 'Direct Sales';
      aggregation[name] = (aggregation[name] || 0) + so.calculatedTotal;
    });

    chart.setOption({
      title: { text: 'Sales by Broker', left: 'center' },
      tooltip: { trigger: 'item', formatter: (p) => `${p.name}: ₹${p.value.toLocaleString()} (${p.percent}%)` },
      series: [{
        type: 'pie',
        radius: ['40%', '60%'],
        data: Object.entries(aggregation).map(([name, value]) => ({ name, value })),
        emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.5)' } }
      }]
    });
  };

  return (
    <Layout title="Analytics Dashboard">
      <div className="p-4 bg-light min-vh-100">
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-1 fw-bold text-dark">Business Analytics</h2>
            <p className="text-muted">Real-time visualization of sales, procurement, and inventory</p>
          </Col>
          <Col xs="auto">
            <Button variant="white" className="shadow-sm border" onClick={fetchDashboardData} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : <><FiRefreshCw className="me-2" /> Sync Data</>}
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted fw-medium">Aggregating business metrics...</p>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <Row className="mb-4 g-3">
              <Col md={3}>
                <Card className="border-0 shadow-sm overflow-hidden h-100">
                  <div className="bg-primary py-1"></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted text-uppercase fw-bold mb-1 d-block">Sales Revenue</small>
                        <h3 className="mb-0 fw-bold">₹{data.salesOrders.reduce((s, o) => s + o.calculatedTotal, 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</h3>
                        <Badge bg="soft-primary" className="text-primary mt-2">
                          <FiActivity className="me-1"/> {data.salesOrders.length} Orders
                        </Badge>
                      </div>
                      <div className="bg-soft-primary p-3 rounded-circle text-primary">
                        <FiTrendingUp size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm overflow-hidden h-100">
                  <div className="bg-success py-1"></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted text-uppercase fw-bold mb-1 d-block">Total Procurement</small>
                        <h3 className="mb-0 fw-bold">₹{data.purchaseOrders.reduce((s, o) => s + o.calculatedTotal, 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</h3>
                        <Badge bg="soft-success" className="text-success mt-2">
                          <FiShoppingBag className="me-1"/> {data.purchaseOrders.length} POs
                        </Badge>
                      </div>
                      <div className="bg-soft-success p-3 rounded-circle text-success">
                        <FiBarChart2 size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm overflow-hidden h-100">
                  <div className="bg-info py-1"></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted text-uppercase fw-bold mb-1 d-block">Inventory Value</small>
                        <h3 className="mb-0 fw-bold">{data.stock.length}</h3>
                        <div className="text-info small fw-medium mt-2">
                          <FiPackage className="me-1"/> {data.stock.reduce((s, o) => s + (o.totalAvailableStock || 0), 0).toLocaleString()} Units
                        </div>
                      </div>
                      <div className="bg-soft-info p-3 rounded-circle text-info">
                        <FiPackage size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm overflow-hidden h-100">
                  <div className="bg-warning py-1"></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted text-uppercase fw-bold mb-1 d-block">Active Logistics</small>
                        <h3 className="mb-0 fw-bold">{data.salesOrders.filter(o => o.status === 'Dispatched' || o.status === 'Loaded').length}</h3>
                        <div className="text-warning small fw-medium mt-2">
                          <FiTruck className="me-1"/> In-Transit Orders
                        </div>
                      </div>
                      <div className="bg-soft-warning p-3 rounded-circle text-warning">
                        <FiTruck size={24} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Main Charts Grid */}
            <Row className="g-4">
              {/* Row 1: Trends and Pipeline */}
              <Col lg={8}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div id="orderTrends" className="echart-container" style={{ width: '100%', height: '400px' }}></div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div id="salesPipeline" className="echart-container" style={{ width: '100%', height: '400px' }}></div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Row 2: Top Parties */}
              <Col lg={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div id="salesByBuyer" className="echart-container" style={{ width: '100%', height: '350px' }}></div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div id="purchasesByVendor" className="echart-container" style={{ width: '100%', height: '350px' }}></div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Row 3: Product Analysis */}
              <Col lg={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div id="stockHealth" className="echart-container" style={{ width: '100%', height: '350px' }}></div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={8}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div id="topProducts" className="echart-container" style={{ width: '100%', height: '350px' }}></div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Row 4: Stock Composition and Broker Performance */}
              <Col lg={8}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div id="stockComp" className="echart-container" style={{ width: '100%', height: '400px' }}></div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <div id="brokerPerformance" className="echart-container" style={{ width: '100%', height: '400px' }}></div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ReportingDashboard;
